import {
  CaptureUpdateAction,
  getSceneVersion,
  restoreElements,
  zoomToFitBounds,
  reconcileElements,
} from "@excalidraw/excalidraw";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { APP_NAME, cloneJSON, EVENT, toBrandedType } from "@excalidraw/common";
import {
  IDLE_THRESHOLD,
  ACTIVE_THRESHOLD,
  UserIdleState,
  assertNever,
  isDevEnv,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  throttleRAF,
} from "@excalidraw/common";
import { decryptData } from "@excalidraw/excalidraw/data/encryption";
import { getVisibleSceneBounds } from "@excalidraw/element";
import { newElementWith } from "@excalidraw/element";
import { isImageElement, isInitializedImageElement } from "@excalidraw/element";
import { AbortError } from "@excalidraw/excalidraw/errors";
import { t } from "@excalidraw/excalidraw/i18n";
import { withBatchedUpdates } from "@excalidraw/excalidraw/reactUtils";

import throttle from "lodash.throttle";
import { PureComponent } from "react";

import { bumpElementVersions } from "@excalidraw/excalidraw/data/restore";

import type {
  ReconciledExcalidrawElement,
  RemoteExcalidrawElement,
} from "@excalidraw/excalidraw/data/reconcile";
import type { ImportedDataState } from "@excalidraw/excalidraw/data/types";
import type {
  ExcalidrawElement,
  FileId,
  InitializedExcalidrawImageElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  BinaryFileData,
  ExcalidrawImperativeAPI,
  SocketId,
  Collaborator,
  Gesture,
} from "@excalidraw/excalidraw/types";
import type { Mutable, ValueOf } from "@excalidraw/common/utility-types";

import { appJotaiStore, atom } from "../app-jotai";
import {
  CURSOR_SYNC_TIMEOUT,
  FILE_UPLOAD_MAX_BYTES,
  FIREBASE_STORAGE_PREFIXES,
  INITIAL_SCENE_UPDATE_TIMEOUT,
  LOAD_IMAGES_TIMEOUT,
  WS_SUBTYPES,
  SYNC_FULL_SCENE_INTERVAL_MS,
  WS_EVENTS,
} from "../app_constants";
import {
  generateCollaborationLinkData,
  getCollaborationBasePath,
  getCollaborationLink,
  getSyncableElements,
} from "../data";
import {
  encodeFilesForUpload,
  FileManager,
  updateStaleImageStatuses,
} from "../data/FileManager";
import { FileStatusStore } from "../data/fileStatusStore";
import { LocalData } from "../data/LocalData";
import { loadFilesFromFirebase, saveFilesToFirebase } from "../data/firebase";
import {
  importGuestIdFromLocalStorage,
  importUsernameFromLocalStorage,
  saveCollaborationIdentityToLocalStorage,
} from "../data/localStorage";
import { resetBrowserStateVersions } from "../data/tabSync";

import { collabErrorIndicatorAtom } from "./CollabError";
import Portal from "./Portal";
import {
  KindrawCollabSocket,
  type KindrawCollabParticipant,
} from "./KindrawCollabSocket";

import type {
  SocketUpdateDataSource,
  SyncableExcalidrawElement,
} from "../data";

export const collabAPIAtom = atom<CollabAPI | null>(null);
export const isCollaboratingAtom = atom(false);
export const isOfflineAtom = atom(false);

export type CollabUserProfile = {
  userId: string | null;
  username: string;
  avatarUrl: string | null;
  githubLogin: string | null;
};

interface CollabState {
  errorMessage: string | null;
  /** errors related to saving */
  dialogNotifiedErrors: Record<string, boolean>;
  username: string;
  userId: string | null;
  avatarUrl: string | null;
  githubLogin: string | null;
  activeRoomLink: string | null;
}

export const activeRoomLinkAtom = atom<string | null>(null);

type CollabInstance = InstanceType<typeof Collab>;

export interface CollabAPI {
  /** function so that we can access the latest value from stale callbacks */
  isCollaborating: () => boolean;
  onPointerUpdate: CollabInstance["onPointerUpdate"];
  startCollaboration: CollabInstance["startCollaboration"];
  stopCollaboration: CollabInstance["stopCollaboration"];
  syncElements: CollabInstance["syncElements"];
  seedCollaborationScene: CollabInstance["seedCollaborationScene"];
  fetchImageFilesFromFirebase: CollabInstance["fetchImageFilesFromFirebase"];
  setUsername: CollabInstance["setUsername"];
  getUsername: CollabInstance["getUsername"];
  setUserProfile: CollabInstance["setUserProfile"];
  getUserProfile: CollabInstance["getUserProfile"];
  getActiveRoomLink: CollabInstance["getActiveRoomLink"];
  setCollabError: CollabInstance["setErrorDialog"];
}

type CollaborationLinkData = {
  roomId: string;
  roomKey: string;
};

const createGuestCollaborationId = () =>
  `guest-${Math.random().toString(36).slice(2, 10)}`;

const createGuestCollaborationName = () =>
  `Guest ${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

interface CollabProps {
  excalidrawAPI: ExcalidrawImperativeAPI;
}

class Collab extends PureComponent<CollabProps, CollabState> {
  portal: Portal;
  fileManager: FileManager;
  excalidrawAPI: CollabProps["excalidrawAPI"];
  activeIntervalId: number | null;
  idleTimeoutId: number | null;

  private socketInitializationTimer?: number;
  private lastBroadcastedOrReceivedSceneVersion: number = -1;
  private collaborators = new Map<SocketId, Collaborator>();
  private pendingSeededScene: readonly SyncableExcalidrawElement[] | null =
    null;

  constructor(props: CollabProps) {
    super(props);
    this.state = {
      errorMessage: null,
      dialogNotifiedErrors: {},
      username: importUsernameFromLocalStorage() || "",
      userId: null,
      avatarUrl: null,
      githubLogin: null,
      activeRoomLink: null,
    };
    this.portal = new Portal(this);
    this.fileManager = new FileManager({
      onFileStatusChange: FileStatusStore.updateStatuses.bind(FileStatusStore),
      getFiles: async (fileIds) => {
        const { roomId, roomKey } = this.portal;
        if (!roomId || !roomKey) {
          throw new AbortError();
        }

        return loadFilesFromFirebase(`files/rooms/${roomId}`, roomKey, fileIds);
      },
      saveFiles: async ({ addedFiles }) => {
        const { roomId, roomKey } = this.portal;
        if (!roomId || !roomKey) {
          throw new AbortError();
        }

        const { savedFiles, erroredFiles } = await saveFilesToFirebase({
          prefix: `${FIREBASE_STORAGE_PREFIXES.collabFiles}/${roomId}`,
          files: await encodeFilesForUpload({
            files: addedFiles,
            encryptionKey: roomKey,
            maxBytes: FILE_UPLOAD_MAX_BYTES,
          }),
        });

        return {
          savedFiles: savedFiles.reduce(
            (acc: Map<FileId, BinaryFileData>, id) => {
              const fileData = addedFiles.get(id);
              if (fileData) {
                acc.set(id, fileData);
              }
              return acc;
            },
            new Map(),
          ),
          erroredFiles: erroredFiles.reduce(
            (acc: Map<FileId, BinaryFileData>, id) => {
              const fileData = addedFiles.get(id);
              if (fileData) {
                acc.set(id, fileData);
              }
              return acc;
            },
            new Map(),
          ),
        };
      },
    });
    this.excalidrawAPI = props.excalidrawAPI;
    this.activeIntervalId = null;
    this.idleTimeoutId = null;
  }

  private onUmmount: (() => void) | null = null;

  componentDidMount() {
    window.addEventListener(EVENT.BEFORE_UNLOAD, this.beforeUnload);
    window.addEventListener("online", this.onOfflineStatusToggle);
    window.addEventListener("offline", this.onOfflineStatusToggle);
    window.addEventListener(EVENT.UNLOAD, this.onUnload);

    const unsubOnUserFollow = this.excalidrawAPI.onUserFollow((payload) => {
      this.portal.socket && this.portal.broadcastUserFollowed(payload);
    });
    const throttledRelayUserViewportBounds = throttleRAF(
      this.relayVisibleSceneBounds,
    );
    const unsubOnScrollChange = this.excalidrawAPI.onScrollChange(() =>
      throttledRelayUserViewportBounds(),
    );
    this.onUmmount = () => {
      unsubOnUserFollow();
      unsubOnScrollChange();
    };

    this.onOfflineStatusToggle();

    const collabAPI: CollabAPI = {
      isCollaborating: this.isCollaborating,
      onPointerUpdate: this.onPointerUpdate,
      startCollaboration: this.startCollaboration,
      syncElements: this.syncElements,
      seedCollaborationScene: this.seedCollaborationScene,
      fetchImageFilesFromFirebase: this.fetchImageFilesFromFirebase,
      stopCollaboration: this.stopCollaboration,
      setUsername: this.setUsername,
      getUsername: this.getUsername,
      setUserProfile: this.setUserProfile,
      getUserProfile: this.getUserProfile,
      getActiveRoomLink: this.getActiveRoomLink,
      setCollabError: this.setErrorDialog,
    };

    appJotaiStore.set(collabAPIAtom, collabAPI);

    if (isTestEnv() || isDevEnv()) {
      window.collab = window.collab || ({} as Window["collab"]);
      Object.defineProperties(window, {
        collab: {
          configurable: true,
          value: this,
        },
      });
    }
  }

  onOfflineStatusToggle = () => {
    appJotaiStore.set(isOfflineAtom, !window.navigator.onLine);
  };

  componentWillUnmount() {
    window.removeEventListener("online", this.onOfflineStatusToggle);
    window.removeEventListener("offline", this.onOfflineStatusToggle);
    window.removeEventListener(EVENT.BEFORE_UNLOAD, this.beforeUnload);
    window.removeEventListener(EVENT.UNLOAD, this.onUnload);
    window.removeEventListener(EVENT.POINTER_MOVE, this.onPointerMove);
    window.removeEventListener(
      EVENT.VISIBILITY_CHANGE,
      this.onVisibilityChange,
    );
    if (this.activeIntervalId) {
      window.clearInterval(this.activeIntervalId);
      this.activeIntervalId = null;
    }
    if (this.idleTimeoutId) {
      window.clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }
    this.onUmmount?.();
  }

  isCollaborating = () => appJotaiStore.get(isCollaboratingAtom)!;

  private setIsCollaborating = (isCollaborating: boolean) => {
    appJotaiStore.set(isCollaboratingAtom, isCollaborating);
  };

  private onUnload = () => {
    this.destroySocketClient({ isUnload: true });
  };

  private beforeUnload = withBatchedUpdates((event: BeforeUnloadEvent) => {
    const syncableElements = getSyncableElements(
      this.getSceneElementsIncludingDeleted(),
    );

    if (
      this.isCollaborating() &&
      (this.fileManager.shouldPreventUnload(syncableElements) ||
        !this.portal.isSceneSaved(syncableElements))
    ) {
      // this won't run in time if user decides to leave the site, but
      //  the purpose is to run in immediately after user decides to stay
      this.saveCollabRoomToFirebase(syncableElements);

      if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
        preventUnload(event);
      } else {
        console.warn(
          "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
        );
      }
    }
  });

  saveCollabRoomToFirebase = async (
    syncableElements: readonly SyncableExcalidrawElement[],
  ) => {
    syncableElements = cloneJSON(syncableElements);
    try {
      await this.portal.persistSnapshot(syncableElements);

      this.resetErrorIndicator();
    } catch (error: any) {
      const errorMessage = t("errors.collabSaveFailed");

      if (
        !this.state.dialogNotifiedErrors[errorMessage] ||
        !this.isCollaborating()
      ) {
        this.setErrorDialog(errorMessage);
        this.setState({
          dialogNotifiedErrors: {
            ...this.state.dialogNotifiedErrors,
            [errorMessage]: true,
          },
        });
      }

      if (this.isCollaborating()) {
        this.setErrorIndicator(errorMessage);
      }

      console.error(error);
    }
  };

  stopCollaboration = (keepRemoteState = true) => {
    this.queueBroadcastAllElements.cancel();
    this.queueSaveToFirebase.cancel();
    this.loadImageFiles.cancel();
    this.resetErrorIndicator(true);
    this.pendingSeededScene = null;

    this.saveCollabRoomToFirebase(
      getSyncableElements(
        this.excalidrawAPI.getSceneElementsIncludingDeleted(),
      ),
    );

    this.clearBootstrapErrorHandler();

    if (!keepRemoteState) {
      LocalData.fileStorage.reset();
      window.history.replaceState({}, APP_NAME, getCollaborationBasePath());
      this.destroySocketClient();
    } else if (window.confirm(t("alerts.collabStopOverridePrompt"))) {
      // hack to ensure that we prefer we disregard any new browser state
      // that could have been saved in other tabs while we were collaborating
      resetBrowserStateVersions();

      window.history.pushState({}, APP_NAME, getCollaborationBasePath());
      this.destroySocketClient();

      LocalData.fileStorage.reset();

      const elements = this.excalidrawAPI
        .getSceneElementsIncludingDeleted()
        .map((element) => {
          if (isImageElement(element) && element.status === "saved") {
            return newElementWith(element, { status: "pending" });
          }
          return element;
        });

      this.excalidrawAPI.updateScene({
        elements,
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    }
  };

  private destroySocketClient = (opts?: { isUnload: boolean }) => {
    this.lastBroadcastedOrReceivedSceneVersion = -1;
    this.clearBootstrapErrorHandler();
    this.portal.close();
    this.fileManager.reset();
    if (!opts?.isUnload) {
      this.setIsCollaborating(false);
      this.setActiveRoomLink(null);
      this.collaborators = new Map();
      this.excalidrawAPI.updateScene({
        collaborators: this.collaborators,
      });
      LocalData.resumeSave("collaboration");
    }
  };

  private fetchImageFilesFromFirebase = async (opts: {
    elements: readonly ExcalidrawElement[];
    /**
     * Indicates whether to fetch files that are errored or pending and older
     * than 10 seconds.
     *
     * Use this as a mechanism to fetch files which may be ok but for some
     * reason their status was not updated correctly.
     */
    forceFetchFiles?: boolean;
  }) => {
    const unfetchedImages = opts.elements
      .filter((element) => {
        return (
          isInitializedImageElement(element) &&
          !this.fileManager.isFileTracked(element.fileId) &&
          !element.isDeleted &&
          (opts.forceFetchFiles
            ? element.status !== "pending" ||
              Date.now() - element.updated > 10000
            : element.status === "saved")
        );
      })
      .map((element) => (element as InitializedExcalidrawImageElement).fileId);

    return await this.fileManager.getFiles(unfetchedImages);
  };

  private decryptPayload = async (
    iv: Uint8Array<ArrayBuffer>,
    encryptedData: ArrayBuffer,
    decryptionKey: string,
  ): Promise<ValueOf<SocketUpdateDataSource>> => {
    try {
      const decrypted = await decryptData(iv, encryptedData, decryptionKey);

      const decodedData = new TextDecoder("utf-8").decode(
        new Uint8Array(decrypted),
      );
      return JSON.parse(decodedData);
    } catch (error) {
      window.alert(t("alerts.decryptFailed"));
      console.error(error);
      return {
        type: WS_SUBTYPES.INVALID_RESPONSE,
      };
    }
  };

  private getCurrentCollaboratorIdentity = (): Partial<Collaborator> => ({
    username: this.state.username || undefined,
    id: this.state.userId || undefined,
    avatarUrl: this.state.avatarUrl || undefined,
  });

  private syncCurrentCollaborator = () => {
    if (!this.portal.socket?.id) {
      return;
    }

    this.updateCollaborator(
      this.portal.socket.id as SocketId,
      this.getCurrentCollaboratorIdentity(),
    );
  };

  broadcastPresence = () => {
    this.syncCurrentCollaborator();

    if (!this.isCollaborating()) {
      return;
    }

    void this.portal.broadcastIdleChange(
      document.hidden ? UserIdleState.AWAY : UserIdleState.ACTIVE,
    );
  };

  private ensureCollaborationIdentity = () => {
    const currentUsername = this.state.username.trim();
    const persistedGuestId = importGuestIdFromLocalStorage();

    if (currentUsername) {
      if (
        !this.state.githubLogin &&
        !this.state.avatarUrl &&
        !this.state.userId
      ) {
        const guestId = persistedGuestId || createGuestCollaborationId();
        this.setState({ userId: guestId }, this.broadcastPresence);
        saveCollaborationIdentityToLocalStorage({
          username: currentUsername,
          guestId,
        });
      }
      return;
    }

    const username =
      importUsernameFromLocalStorage()?.trim() ||
      createGuestCollaborationName();
    const guestId = persistedGuestId || createGuestCollaborationId();

    this.setState(
      {
        username,
        userId: guestId,
        avatarUrl: null,
        githubLogin: null,
      },
      this.broadcastPresence,
    );
    saveCollaborationIdentityToLocalStorage({
      username,
      guestId,
    });
  };

  private fallbackInitializationHandler: null | ((error?: Error) => void) =
    null;

  private clearBootstrapErrorHandler = (socket = this.portal.socket) => {
    if (socket && this.fallbackInitializationHandler) {
      socket.off("connect_error", this.fallbackInitializationHandler);
    }
    this.fallbackInitializationHandler = null;
  };

  private finalizeRoomInitialization = () => {
    clearTimeout(this.socketInitializationTimer!);
    this.clearBootstrapErrorHandler();
    this.portal.socketInitialized = true;
    this.flushSeededScene();
    this.broadcastPresence();
  };

  private syncParticipants = (participants: KindrawCollabParticipant[]) => {
    for (const participant of participants) {
      this.updateCollaborator(participant.socketId, {
        username: participant.username || undefined,
        avatarUrl: participant.avatarUrl || undefined,
        id: participant.userId || undefined,
        userState: participant.userState,
      });
    }
    this.syncCurrentCollaborator();
  };

  private handleDecryptedData = (
    decryptedData: ValueOf<SocketUpdateDataSource>,
    resolveScene: (
      scene:
        | (ImportedDataState & {
            elements: readonly OrderedExcalidrawElement[];
          })
        | null,
    ) => void,
  ) => {
    switch (decryptedData.type) {
      case WS_SUBTYPES.INVALID_RESPONSE:
        return;
      case WS_SUBTYPES.INIT: {
        const remoteElements = toBrandedType<
          readonly RemoteExcalidrawElement[]
        >(decryptedData.payload.elements);
        const reconciledElements = this._reconcileElements(remoteElements);
        if (!this.portal.socketInitialized) {
          this.finalizeRoomInitialization();
          this.handleRemoteSceneUpdate(reconciledElements);
          resolveScene({
            elements: reconciledElements,
            scrollToContent: true,
          });
          return;
        }
        this.handleRemoteSceneUpdate(reconciledElements);
        return;
      }
      case WS_SUBTYPES.UPDATE:
        this.handleRemoteSceneUpdate(
          this._reconcileElements(
            toBrandedType<readonly RemoteExcalidrawElement[]>(
              decryptedData.payload.elements,
            ),
          ),
        );
        return;
      case WS_SUBTYPES.MOUSE_LOCATION: {
        const {
          pointer,
          button,
          username,
          avatarUrl,
          userId,
          selectedElementIds,
        } = decryptedData.payload;
        const socketId: SocketUpdateDataSource["MOUSE_LOCATION"]["payload"]["socketId"] =
          decryptedData.payload.socketId ||
          // @ts-ignore legacy, see #2094 (#2097)
          decryptedData.payload.socketID;

        this.updateCollaborator(socketId, {
          pointer,
          button,
          selectedElementIds,
          username,
          avatarUrl: avatarUrl || undefined,
          id: userId || undefined,
        });
        return;
      }
      case WS_SUBTYPES.USER_VISIBLE_SCENE_BOUNDS: {
        const { sceneBounds, socketId, username, avatarUrl, userId } =
          decryptedData.payload;

        this.updateCollaborator(socketId, {
          username,
          avatarUrl: avatarUrl || undefined,
          id: userId || undefined,
        });

        const appState = this.excalidrawAPI.getAppState();
        if (appState.userToFollow?.socketId !== socketId) {
          console.warn(
            `receiving remote client's (from ${socketId}) viewport bounds even though we're not subscribed to it!`,
          );
          return;
        }

        if (
          appState.userToFollow &&
          appState.followedBy.has(appState.userToFollow.socketId)
        ) {
          return;
        }

        this.excalidrawAPI.updateScene({
          appState: zoomToFitBounds({
            appState,
            bounds: sceneBounds,
            fitToViewport: true,
            viewportZoomFactor: 1,
          }).appState,
        });
        return;
      }
      case WS_SUBTYPES.IDLE_STATUS: {
        const { userState, socketId, username, avatarUrl, userId } =
          decryptedData.payload;
        this.updateCollaborator(socketId, {
          userState,
          username,
          avatarUrl: avatarUrl || undefined,
          id: userId || undefined,
        });
        return;
      }
      default:
        assertNever(decryptedData, null);
    }
  };

  startCollaboration = async (
    existingRoomLinkData: CollaborationLinkData | null,
    opts?: {
      bootstrapFromCurrentScene?: boolean;
    },
  ) => {
    this.ensureCollaborationIdentity();
    this.pendingSeededScene = null;

    if (this.portal.socket) {
      return null;
    }

    let roomId;
    let roomKey;

    if (existingRoomLinkData) {
      ({ roomId, roomKey } = existingRoomLinkData);
      const nextRoomLink = getCollaborationLink({ roomId, roomKey });
      if (window.location.href !== nextRoomLink) {
        window.history.replaceState({}, APP_NAME, nextRoomLink);
      }
    } else {
      ({ roomId, roomKey } = await generateCollaborationLinkData());
      window.history.pushState(
        {},
        APP_NAME,
        getCollaborationLink({ roomId, roomKey }),
      );
    }

    // TODO: `ImportedDataState` type here seems abused
    const scenePromise = resolvablePromise<
      | (ImportedDataState & { elements: readonly OrderedExcalidrawElement[] })
      | null
    >();
    let sceneResolved = false;
    const resolveScene = (
      scene:
        | (ImportedDataState & {
            elements: readonly OrderedExcalidrawElement[];
          })
        | null,
    ) => {
      if (!sceneResolved) {
        sceneResolved = true;
        scenePromise.resolve(scene);
      }
    };

    this.setIsCollaborating(true);
    LocalData.pauseSave("collaboration");

    const fallbackInitializationHandler = (error?: Error) => {
      clearTimeout(this.socketInitializationTimer!);
      this.clearBootstrapErrorHandler();
      if (error) {
        console.error(error);
        this.setErrorDialog(error.message);
      }
      this.destroySocketClient();
      resolveScene(null);
    };
    this.fallbackInitializationHandler = fallbackInitializationHandler;

    if (existingRoomLinkData && !opts?.bootstrapFromCurrentScene) {
      // when joining existing room, don't merge it with current scene data
      this.excalidrawAPI.resetScene();
    } else {
      const elements = this.excalidrawAPI.getSceneElements().map((element) => {
        if (isImageElement(element) && element.status === "saved") {
          return newElementWith(element, { status: "pending" });
        }
        return element;
      });
      // remove deleted elements from elements array to ensure we don't
      // expose potentially sensitive user data in case user manually deletes
      // existing elements (or clears scene), which would otherwise be persisted
      // to database even if deleted before creating the room.
      this.excalidrawAPI.updateScene({
        elements,
        captureUpdate: CaptureUpdateAction.NEVER,
      });

      this.seedCollaborationScene(elements);
    }

    try {
      // Fast room joins can dispatch the first server events before the rest of
      // the startup listeners are registered, so wire them before opening.
      const socket = new KindrawCollabSocket({
        roomId,
        baseUrl: import.meta.env.VITE_APP_WS_SERVER_URL,
        profile: {
          username: this.state.username.trim(),
          avatarUrl: this.state.avatarUrl,
          userId: this.state.userId,
          githubLogin: this.state.githubLogin,
        },
      });

      socket.once("connect_error", fallbackInitializationHandler);

      socket.on("participants", (participants: KindrawCollabParticipant[]) => {
        this.syncParticipants(participants);
      });

      socket.on(
        "snapshot",
        async (encryptedData: ArrayBuffer, iv: Uint8Array<ArrayBuffer>) => {
          if (!this.portal.roomKey) {
            return;
          }

          const decryptedData = await this.decryptPayload(
            iv,
            encryptedData,
            this.portal.roomKey,
          );
          this.handleDecryptedData(decryptedData, resolveScene);
        },
      );

      socket.on(
        "client-broadcast",
        async (encryptedData: ArrayBuffer, iv: Uint8Array<ArrayBuffer>) => {
          if (!this.portal.roomKey) {
            return;
          }

          const decryptedData = await this.decryptPayload(
            iv,
            encryptedData,
            this.portal.roomKey,
          );
          this.handleDecryptedData(decryptedData, resolveScene);
        },
      );

      socket.on("first-in-room", () => {
        if (this.portal.socket) {
          this.portal.socket.off("first-in-room");
        }
        this.finalizeRoomInitialization();
        resolveScene(
          this.pendingSeededScene?.length
            ? {
                elements: this.pendingSeededScene,
                scrollToContent: true,
              }
            : null,
        );
      });

      socket.on(WS_EVENTS.USER_FOLLOW_ROOM_CHANGE, (followedBy: SocketId[]) => {
        this.excalidrawAPI.updateScene({
          appState: { followedBy: new Set(followedBy) },
        });

        this.relayVisibleSceneBounds({ force: true });
      });

      socket.on("reconnected", () => {
        this.portal.socketInitialized = true;
        this.broadcastPresence();
        this.portal.broadcastScene(
          WS_SUBTYPES.INIT,
          this.excalidrawAPI.getSceneElementsIncludingDeleted(),
          true,
        );
        this.queueSaveToFirebase();
        this.resetErrorIndicator();
      });

      socket.on("connect_error", (error: Error) => {
        if (this.portal.socketInitialized) {
          this.setErrorIndicator(error.message);
        }
      });

      this.portal.socket = this.portal.open(socket, roomId, roomKey);
    } catch (error: any) {
      this.clearBootstrapErrorHandler();
      console.error(error);
      this.setErrorDialog(error.message);
      return null;
    }

    this.socketInitializationTimer = window.setTimeout(
      () =>
        fallbackInitializationHandler(
          new Error("Kindraw collaboration room did not initialize in time."),
        ),
      INITIAL_SCENE_UPDATE_TIMEOUT,
    );

    this.initializeIdleDetector();

    this.setActiveRoomLink(window.location.href);

    return scenePromise;
  };

  private _reconcileElements = (
    remoteElements: readonly RemoteExcalidrawElement[],
  ): ReconciledExcalidrawElement[] => {
    const appState = this.excalidrawAPI.getAppState();

    const existingElements = this.getSceneElementsIncludingDeleted();

    // NOTE ideally we restore _after_ reconciliation but we can't do that
    // as we'd regenerate even elements such as appState.newElement which would
    // break the state
    remoteElements = restoreElements(remoteElements, existingElements);

    let reconciledElements = reconcileElements(
      existingElements,
      remoteElements,
      appState,
    );

    reconciledElements = bumpElementVersions(
      reconciledElements,
      existingElements,
    );

    // Avoid broadcasting to the rest of the collaborators the scene
    // we just received!
    // Note: this needs to be set before updating the scene as it
    // synchronously calls render.
    this.setLastBroadcastedOrReceivedSceneVersion(
      getSceneVersion(reconciledElements),
    );

    return reconciledElements;
  };

  private loadImageFiles = throttle(async () => {
    const { loadedFiles, erroredFiles } =
      await this.fetchImageFilesFromFirebase({
        elements: this.excalidrawAPI.getSceneElementsIncludingDeleted(),
      });

    this.excalidrawAPI.addFiles(loadedFiles);

    updateStaleImageStatuses({
      excalidrawAPI: this.excalidrawAPI,
      erroredFiles,
      elements: this.excalidrawAPI.getSceneElementsIncludingDeleted(),
    });
  }, LOAD_IMAGES_TIMEOUT);

  private handleRemoteSceneUpdate = (
    elements: ReconciledExcalidrawElement[],
  ) => {
    this.excalidrawAPI.updateScene({
      elements,
      captureUpdate: CaptureUpdateAction.NEVER,
    });

    this.loadImageFiles();
  };

  private onPointerMove = () => {
    if (this.idleTimeoutId) {
      window.clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }

    this.idleTimeoutId = window.setTimeout(this.reportIdle, IDLE_THRESHOLD);

    if (!this.activeIntervalId) {
      this.activeIntervalId = window.setInterval(
        this.reportActive,
        ACTIVE_THRESHOLD,
      );
    }
  };

  private onVisibilityChange = () => {
    if (document.hidden) {
      if (this.idleTimeoutId) {
        window.clearTimeout(this.idleTimeoutId);
        this.idleTimeoutId = null;
      }
      if (this.activeIntervalId) {
        window.clearInterval(this.activeIntervalId);
        this.activeIntervalId = null;
      }
      this.onIdleStateChange(UserIdleState.AWAY);
    } else {
      this.idleTimeoutId = window.setTimeout(this.reportIdle, IDLE_THRESHOLD);
      this.activeIntervalId = window.setInterval(
        this.reportActive,
        ACTIVE_THRESHOLD,
      );
      this.onIdleStateChange(UserIdleState.ACTIVE);
    }
  };

  private reportIdle = () => {
    this.onIdleStateChange(UserIdleState.IDLE);
    if (this.activeIntervalId) {
      window.clearInterval(this.activeIntervalId);
      this.activeIntervalId = null;
    }
  };

  private reportActive = () => {
    this.onIdleStateChange(UserIdleState.ACTIVE);
  };

  private initializeIdleDetector = () => {
    document.addEventListener(EVENT.POINTER_MOVE, this.onPointerMove);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, this.onVisibilityChange);
  };

  setCollaborators(sockets: SocketId[]) {
    const collaborators: InstanceType<typeof Collab>["collaborators"] =
      new Map();
    for (const socketId of sockets) {
      const isCurrentUser = socketId === this.portal.socket?.id;
      collaborators.set(
        socketId,
        Object.assign({}, this.collaborators.get(socketId), {
          ...(isCurrentUser ? this.getCurrentCollaboratorIdentity() : {}),
          isCurrentUser,
        }),
      );
    }
    this.collaborators = collaborators;
    this.excalidrawAPI.updateScene({ collaborators });
  }

  updateCollaborator = (socketId: SocketId, updates: Partial<Collaborator>) => {
    const collaborators = new Map(this.collaborators);
    const user: Mutable<Collaborator> = Object.assign(
      {},
      collaborators.get(socketId),
      updates,
      {
        isCurrentUser: socketId === this.portal.socket?.id,
      },
    );
    collaborators.set(socketId, user);
    this.collaborators = collaborators;

    this.excalidrawAPI.updateScene({
      collaborators,
    });
  };

  public setLastBroadcastedOrReceivedSceneVersion = (version: number) => {
    this.lastBroadcastedOrReceivedSceneVersion = version;
  };

  public getLastBroadcastedOrReceivedSceneVersion = () => {
    return this.lastBroadcastedOrReceivedSceneVersion;
  };

  public getSceneElementsIncludingDeleted = () => {
    return this.excalidrawAPI.getSceneElementsIncludingDeleted();
  };

  onPointerUpdate = throttle(
    (payload: {
      pointer: SocketUpdateDataSource["MOUSE_LOCATION"]["payload"]["pointer"];
      button: SocketUpdateDataSource["MOUSE_LOCATION"]["payload"]["button"];
      pointersMap: Gesture["pointers"];
    }) => {
      payload.pointersMap.size < 2 &&
        this.portal.socket &&
        this.portal.broadcastMouseLocation(payload);
    },
    CURSOR_SYNC_TIMEOUT,
  );

  relayVisibleSceneBounds = (props?: { force: boolean }) => {
    const appState = this.excalidrawAPI.getAppState();

    if (this.portal.socket && (appState.followedBy.size > 0 || props?.force)) {
      this.portal.broadcastVisibleSceneBounds(
        {
          sceneBounds: getVisibleSceneBounds(appState),
        },
        `follow@${this.portal.socket.id}`,
      );
    }
  };

  onIdleStateChange = (userState: UserIdleState) => {
    this.portal.broadcastIdleChange(userState);
  };

  broadcastElements = (elements: readonly OrderedExcalidrawElement[]) => {
    if (
      getSceneVersion(elements) >
      this.getLastBroadcastedOrReceivedSceneVersion()
    ) {
      this.portal.broadcastScene(WS_SUBTYPES.UPDATE, elements, false);
      this.lastBroadcastedOrReceivedSceneVersion = getSceneVersion(elements);
      this.queueBroadcastAllElements();
    }
  };

  syncElements = (elements: readonly OrderedExcalidrawElement[]) => {
    this.broadcastElements(elements);
    this.queueSaveToFirebase();
  };

  queueBroadcastAllElements = throttle(() => {
    this.portal.broadcastScene(
      WS_SUBTYPES.UPDATE,
      this.excalidrawAPI.getSceneElementsIncludingDeleted(),
      true,
    );
    const currentVersion = this.getLastBroadcastedOrReceivedSceneVersion();
    const newVersion = Math.max(
      currentVersion,
      getSceneVersion(this.getSceneElementsIncludingDeleted()),
    );
    this.setLastBroadcastedOrReceivedSceneVersion(newVersion);
  }, SYNC_FULL_SCENE_INTERVAL_MS);

  queueSaveToFirebase = throttle(
    () => {
      if (this.portal.socketInitialized) {
        this.saveCollabRoomToFirebase(
          getSyncableElements(
            this.excalidrawAPI.getSceneElementsIncludingDeleted(),
          ),
        );
      }
    },
    SYNC_FULL_SCENE_INTERVAL_MS,
    { leading: false },
  );

  seedCollaborationScene = (elements: readonly OrderedExcalidrawElement[]) => {
    this.pendingSeededScene = getSyncableElements(elements);
    this.flushSeededScene();
  };

  private flushSeededScene = () => {
    if (!this.portal.socketInitialized || !this.pendingSeededScene) {
      return;
    }

    const elements = this.pendingSeededScene;
    this.pendingSeededScene = null;
    void this.saveCollabRoomToFirebase(elements);
  };

  setUsername = (username: string) => {
    const nextUsername = username.trim();
    const shouldKeepGuestIdentity =
      !this.state.githubLogin && !this.state.avatarUrl && !this.state.userId;
    const guestId = shouldKeepGuestIdentity
      ? importGuestIdFromLocalStorage() || createGuestCollaborationId()
      : this.state.userId;

    this.setState(
      {
        username: nextUsername,
        userId: guestId,
      },
      this.broadcastPresence,
    );
    saveCollaborationIdentityToLocalStorage({
      username: nextUsername,
      guestId,
    });
  };

  getUsername = () => this.state.username;

  setUserProfile = (profile: CollabUserProfile | null) => {
    if (!profile) {
      this.setState(
        {
          username: "",
          userId: null,
          avatarUrl: null,
          githubLogin: null,
        },
        this.broadcastPresence,
      );
      saveCollaborationIdentityToLocalStorage({ username: "" });
      return;
    }

    const nextUsername = profile.username.trim();
    this.setState(
      {
        username: nextUsername,
        userId: profile.userId,
        avatarUrl: profile.avatarUrl,
        githubLogin: profile.githubLogin,
      },
      this.broadcastPresence,
    );
    saveCollaborationIdentityToLocalStorage({ username: nextUsername });
  };

  getUserProfile = (): CollabUserProfile | null => {
    const { userId, username, avatarUrl, githubLogin } = this.state;
    if (!username && !userId && !avatarUrl && !githubLogin) {
      return null;
    }

    return {
      userId,
      username,
      avatarUrl,
      githubLogin,
    };
  };

  setActiveRoomLink = (activeRoomLink: string | null) => {
    this.setState({ activeRoomLink });
    appJotaiStore.set(activeRoomLinkAtom, activeRoomLink);
  };

  getActiveRoomLink = () => this.state.activeRoomLink;

  setErrorIndicator = (errorMessage: string | null) => {
    appJotaiStore.set(collabErrorIndicatorAtom, {
      message: errorMessage,
      nonce: Date.now(),
    });
  };

  resetErrorIndicator = (resetDialogNotifiedErrors = false) => {
    appJotaiStore.set(collabErrorIndicatorAtom, { message: null, nonce: 0 });
    if (resetDialogNotifiedErrors) {
      this.setState({
        dialogNotifiedErrors: {},
      });
    }
  };

  setErrorDialog = (errorMessage: string | null) => {
    this.setState({
      errorMessage,
    });
  };

  render() {
    const { errorMessage } = this.state;

    return (
      <>
        {errorMessage != null && (
          <ErrorDialog onClose={() => this.setErrorDialog(null)}>
            {errorMessage}
          </ErrorDialog>
        )}
      </>
    );
  }
}

declare global {
  interface Window {
    collab: InstanceType<typeof Collab>;
  }
}

if (isTestEnv() || isDevEnv()) {
  window.collab = window.collab || ({} as Window["collab"]);
}

export default Collab;

export type TCollabClass = Collab;
